import { useState } from 'react';

// The review card's footer: helpfulness voting, the reader "Report" menu, and
// the venue-manager moderation flag. Extracted from ReviewCard (#314) — it
// owns only the report-menu open state; every action is delegated upward.
export interface ReviewCardActionsProps {
  reviewId: string;
  helpfulCount: number;
  notHelpfulCount: number;
  flaggedForModeration: boolean;
  onVote?: (reviewId: string, voteType: 'helpful' | 'notHelpful') => void;
  onFlag?: (reviewId: string, flagType: 'OFFENSIVE' | 'INAPPROPRIATE' | 'FAKE') => void;
  onVenueManagerFlag?: (reviewId: string) => void;
  isVenueManager: boolean;
  userVoteStatus?: { hasVoted: boolean; voteType: 'helpful' | 'notHelpful' | null };
  isVoting: boolean;
  isOwnReview: boolean;
}

export const ReviewCardActions = ({
  reviewId,
  helpfulCount,
  notHelpfulCount,
  flaggedForModeration,
  onVote,
  onFlag,
  onVenueManagerFlag,
  isVenueManager,
  userVoteStatus,
  isVoting,
  isOwnReview,
}: ReviewCardActionsProps) => {
  const [showFlagMenu, setShowFlagMenu] = useState(false);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-100">
      {/* Helpfulness voting - hidden for own reviews */}
      {isOwnReview ? (
        <span className="text-sm text-gray-400 italic">You wrote this review</span>
      ) : (
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-500">Was this helpful?</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onVote?.(reviewId, 'helpful')}
            disabled={isVoting || userVoteStatus?.voteType === 'helpful'}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
              userVoteStatus?.voteType === 'helpful'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label="Mark as helpful"
            aria-pressed={userVoteStatus?.voteType === 'helpful'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
            <span>{helpfulCount}</span>
          </button>
          <button
            type="button"
            onClick={() => onVote?.(reviewId, 'notHelpful')}
            disabled={isVoting || userVoteStatus?.voteType === 'notHelpful'}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
              userVoteStatus?.voteType === 'notHelpful'
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label="Mark as not helpful"
            aria-pressed={userVoteStatus?.voteType === 'notHelpful'}
          >
            <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
            <span>{notHelpfulCount}</span>
          </button>
        </div>
      </div>
      )}

      {/* Flag button */}
      {onFlag && !flaggedForModeration && (
        <div className="relative" onMouseLeave={() => setShowFlagMenu(false)}>
          <button
            type="button"
            onClick={() => setShowFlagMenu(!showFlagMenu)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Report this review"
            aria-expanded={showFlagMenu}
            aria-haspopup="menu"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
              />
            </svg>
            <span>Report</span>
          </button>

          {/* Flag dropdown menu */}
          {showFlagMenu && (
            <div
              className="absolute right-0 mt-1 w-48 bg-gray-50 border border-gray-200 rounded-lg shadow-lg z-10"
              role="menu"
            >
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    onFlag(reviewId, 'OFFENSIVE');
                    setShowFlagMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                >
                  Offensive content
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onFlag(reviewId, 'INAPPROPRIATE');
                    setShowFlagMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                >
                  Inappropriate content
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onFlag(reviewId, 'FAKE');
                    setShowFlagMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                >
                  Fake review
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Venue Manager Flag button */}
      {isVenueManager && onVenueManagerFlag && !flaggedForModeration && (
        <button
          type="button"
          onClick={() => onVenueManagerFlag(reviewId)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 transition-colors"
          aria-label="Flag for moderation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>Flag for Moderation</span>
        </button>
      )}
    </div>
  );
};
